import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../LoadingSpinner';

/**
 * AdminRoute Component
 * 
 * A route wrapper that ensures the user is authenticated AND has admin privileges.
 * If the user is not authenticated, they are redirected to the login page.
 * If the user is authenticated but not an admin or developer, they are redirected to the home page.
 * Shows a loading spinner while checking authentication status.
 * Now also allows developers to access admin routes.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if user is an admin or developer
 * @returns {JSX.Element} The admin content, a redirect to login, or a redirect to home
 */
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Allow super_admin and developer roles
  if (user?.role !== 'super_admin' && user?.role !== 'developer') {
    return <Navigate to="/" />;
  }

  // Render children if authenticated and user has appropriate role
  return children;
};

export default AdminRoute; 